import { Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { BookOpen, X } from 'lucide-react';
import { PAGINA_INICIAL, paginaPorSlug, type PaginaConSeccion } from './content/registry';
import { DocsBreadcrumbs, DocsHeader, DocsPrevNext, DocsSearch, DocsSidebar, DocsToc } from './components/DocsChrome';
import { useDocsTheme } from './useDocsTheme';
import { PortadaPage } from './pages/PortadaPage';
import './docs.css';

/**
 * El centro de ayuda.
 *
 * Vive en su propio arbol de rutas para que el marco -menu, barra, buscador-
 * se monte una vez y no se reconstruya al cambiar de pagina: asi el
 * desplazamiento del menu lateral no salta al navegar.
 *
 * Se lee SIN sesion, igual que la ayuda de las demas aplicaciones. Nada de
 * aqui pide datos al servidor: el contenido es estatico, asi que no hay
 * ninguna llamada que pudiera devolver un 401 ni filtrar informacion de nadie.
 * Eso importa: quien no puede entrar es justo quien mas necesita leer como se
 * entra.
 */
export function DocsApp(): JSX.Element {
  return (
    <Routes>
      <Route index element={<Portada />} />
      <Route path=":slug" element={<PaginaDeAyuda />} />
      <Route path="*" element={<NoEncontrada />} />
    </Routes>
  );
}

/* --------------------------------------------------------------- marco --- */

/**
 * Tres columnas en escritorio, dos en tableta y una en movil:
 *
 *   escritorio  menu · contenido · tabla de contenidos
 *   tableta     menu desplegable · contenido
 *   movil       contenido, con el menu detras del boton de hamburguesa
 *
 * La tabla de contenidos es lo primero que se va porque es lo unico
 * prescindible: es un atajo dentro de una pagina que ya se puede recorrer
 * desplazandose. El menu, en cambio, es la unica forma de llegar a las demas.
 *
 * El ancho del texto se queda en 46rem. Una linea de mas de noventa caracteres
 * se lee peor porque al saltar de renglon cuesta encontrar el siguiente, y en
 * una pantalla ancha llegaria a los 200.
 */
function DocsLayout({ children, slug }: { children: ReactNode; slug: string }): JSX.Element {
  const { tema, alternar } = useDocsTheme();
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [buscadorAbierto, setBuscadorAbierto] = useState(false);
  const articulo = useRef<HTMLElement>(null);
  const { pathname, hash } = useLocation();

  /* Ctrl/Cmd + K abre el buscador. */
  useEffect(() => {
    const alPulsar = (evento: KeyboardEvent): void => {
      if ((evento.ctrlKey || evento.metaKey) && evento.key.toLowerCase() === 'k') {
        evento.preventDefault();
        setBuscadorAbierto((v) => !v);
      }
    };
    window.addEventListener('keydown', alPulsar);
    return () => window.removeEventListener('keydown', alPulsar);
  }, []);

  /* Cambiar de pagina cierra el menu movil y sube arriba del todo. */
  useEffect(() => {
    setMenuAbierto(false);
    if (!hash) window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname, hash]);

  /*
   * Con el menu movil abierto se bloquea el desplazamiento del fondo: si no,
   * al arrastrar sobre el panel se mueve la pagina de detras y el menu parece
   * roto.
   */
  useEffect(() => {
    if (!menuAbierto) return undefined;
    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previo;
    };
  }, [menuAbierto]);

  return (
    <div
      data-docs-tema={tema}
      className="dk-suave min-h-screen"
      style={{ background: 'var(--dk-fondo)' }}
    >
      {/*
        Salto al contenido: con un menu de doce enlaces, llegar al texto con el
        teclado serian doce tabulaciones en cada pagina.
      */}
      <a
        href="#dk-contenido"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded-lg focus:px-3 focus:py-2"
        style={{ background: 'var(--dk-acento-boton)', color: '#fff' }}
      >
        Saltar al contenido
      </a>

      <DocsHeader
        tema={tema}
        onAlternarTema={alternar}
        onBuscar={() => setBuscadorAbierto(true)}
        onMenu={() => setMenuAbierto(true)}
      />

      <div className="mx-auto flex max-w-[1440px] gap-8 px-3 sm:px-5">
        <aside className="hidden w-[260px] shrink-0 lg:block" aria-label="Navegacion de la ayuda">
          <div className="sticky top-14 max-h-[calc(100vh-3.5rem)] overflow-y-auto py-6 pr-2">
            <DocsSidebar />
          </div>
        </aside>

        <main id="dk-contenido" ref={articulo} className="min-w-0 flex-1 py-8" style={{ maxWidth: '46rem' }}>
          {children}
        </main>

        <aside className="hidden w-[220px] shrink-0 xl:block">
          <div className="sticky top-14 max-h-[calc(100vh-3.5rem)] overflow-y-auto py-8">
            <DocsToc contenedor={articulo} slug={slug} />
          </div>
        </aside>
      </div>

      {menuAbierto ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0"
            style={{ background: 'rgba(15,23,42,0.45)' }}
            onClick={() => setMenuAbierto(false)}
            aria-hidden
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Navegacion de la ayuda"
            className="absolute inset-y-0 left-0 w-[min(300px,85vw)] overflow-y-auto border-r p-4"
            style={{ background: 'var(--dk-fondo)', borderColor: 'var(--dk-borde)' }}
          >
            <div className="mb-4 flex items-center justify-between">
              <span className="text-[13px] font-semibold" style={{ color: 'var(--dk-texto)' }}>
                Centro de ayuda
              </span>
              <button
                type="button"
                onClick={() => setMenuAbierto(false)}
                aria-label="Cerrar el menu"
                className="rounded p-1"
                style={{ color: 'var(--dk-texto-tenue)' }}
              >
                <X size={17} />
              </button>
            </div>
            <DocsSidebar alNavegar={() => setMenuAbierto(false)} />
          </div>
        </div>
      ) : null}

      <DocsSearch abierto={buscadorAbierto} onCerrar={() => setBuscadorAbierto(false)} />
    </div>
  );
}

/* -------------------------------------------------------------- paginas --- */

function tituloDePestana(titulo: string): void {
  document.title = `${titulo} · Ayuda | ED Ecosystem`;
}

function Portada(): JSX.Element {
  useEffect(() => tituloDePestana('Centro de ayuda'), []);

  return (
    <DocsLayout slug="portada">
      <PortadaPage />
    </DocsLayout>
  );
}

function PaginaDeAyuda(): JSX.Element {
  const { slug } = useParams();
  const pagina = paginaPorSlug(slug);

  if (!pagina) return <NoEncontrada />;

  return (
    <DocsLayout slug={pagina.slug}>
      {/*
        La `key` del Suspense obliga a montar de nuevo al cambiar de pagina.
        Sin ella, React reutilizaria el componente anterior mientras llega el
        nuevo y se veria el contenido de la pagina que se acaba de dejar bajo
        el titulo de la siguiente.
      */}
      <Suspense key={pagina.slug} fallback={<Esqueleto />}>
        <Envoltorio pagina={pagina} />
      </Suspense>
    </DocsLayout>
  );
}

/**
 * Encabezado y pie comunes de una pagina.
 *
 * Cada pagina se limita a decir su contenido; el titulo, las migas, el titulo
 * de la pestana y los enlaces de anterior/siguiente los pone esto a partir del
 * registro. Asi una pagina nueva no puede olvidarse de ninguno.
 */
function Envoltorio({ pagina }: { pagina: PaginaConSeccion }): JSX.Element {
  const { Componente } = pagina;

  useEffect(() => tituloDePestana(pagina.titulo), [pagina.titulo]);

  return (
    <article>
      <DocsBreadcrumbs seccion={pagina.seccion} titulo={pagina.titulo} />

      <h1 className="text-[30px] font-bold leading-tight tracking-tight" style={{ color: 'var(--dk-texto)' }}>
        {pagina.titulo}
      </h1>
      <p className="mt-2 text-[15.5px] leading-relaxed" style={{ color: 'var(--dk-texto-tenue)' }}>
        {pagina.descripcion}
      </p>

      <div className="mt-8">
        <Componente />
      </div>

      <DocsPrevNext slug={pagina.slug} />
    </article>
  );
}

/**
 * Mientras llega una pagina.
 *
 * Se pintan unas barras del tamano aproximado del titulo y los primeros
 * parrafos en vez de un texto de «cargando»: el salto visual es menor y no hay
 * una palabra que aparezca y desaparezca en cada navegacion.
 */
function Esqueleto(): JSX.Element {
  return (
    <div className="animate-pulse" aria-hidden>
      <div className="h-3 w-40 rounded" style={{ background: 'var(--dk-borde)' }} />
      <div className="mt-4 h-8 w-2/3 rounded" style={{ background: 'var(--dk-borde)' }} />
      <div className="mt-3 h-4 w-full rounded" style={{ background: 'var(--dk-borde)' }} />
      <div className="mt-8 space-y-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-4 rounded" style={{ background: 'var(--dk-borde)' }} />
        ))}
      </div>
    </div>
  );
}

function NoEncontrada(): JSX.Element {
  useEffect(() => tituloDePestana('Pagina no encontrada'), []);

  return (
    <DocsLayout slug="no-encontrada">
      <div className="py-10 text-center">
        <BookOpen size={40} className="mx-auto" style={{ color: 'var(--dk-borde-fuerte)' }} aria-hidden />
        <h1 className="mt-4 text-[24px] font-bold" style={{ color: 'var(--dk-texto)' }}>
          Pagina no encontrada
        </h1>
        <p className="mx-auto mt-2 max-w-md text-[14px]" style={{ color: 'var(--dk-texto-tenue)' }}>
          Esta direccion no existe en la ayuda. Puede que el enlace este mal escrito o que la
          pagina haya cambiado de sitio.
        </p>
        <Link
          to={PAGINA_INICIAL.ruta}
          className="mt-6 inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-[14px] font-medium"
          style={{ background: 'var(--dk-acento-boton)', color: '#fff' }}
        >
          <BookOpen size={15} />
          Volver a la ayuda
        </Link>
      </div>
    </DocsLayout>
  );
}
